// Wrappers para /api/grammar-banks/*.

import { apiFetch } from './api';

export interface GrammarCategory {
  id: number;
  slug: string;
  label: string;
  order_index: number;
}

export interface GrammarStatement {
  id: number;
  slug: string;
  label: string;
  order_index: number;
}

export interface GrammarBankListItem {
  id: number;
  title: string;
  slug: string;
  content: string | null;
  cover_image_url: string | null;
  level_id: number | null;
  level_slug: string | null;
  level_label: string | null;
  category_id: number | null;
  category_slug: string | null;
  category_label: string | null;
  is_published: number;
  created_at: string;
  updated_at: string;
}

export interface GrammarBankActivity {
  id: number;
  grammar_bank_id: number;
  position: number;
  statement_id: number | null;
  statement_slug: string | null;
  statement_label: string | null;
  content: string | null;
  quiz_id: number | null;
  quiz_title: string | null;
}

export interface GrammarBankDetail {
  item: GrammarBankListItem;
  activities: GrammarBankActivity[];
}

interface ListFilters {
  level_id?: number;
  category_id?: number;
  search?: string;
}

function toQS(f: ListFilters): string {
  const u = new URLSearchParams();
  if (f.level_id) u.set('level_id', String(f.level_id));
  if (f.category_id) u.set('category_id', String(f.category_id));
  if (f.search && f.search.trim()) u.set('search', f.search.trim());
  const qs = u.toString();
  return qs ? `?${qs}` : '';
}

export async function listGrammarBanks(filters: ListFilters = {}): Promise<GrammarBankListItem[]> {
  const r = await apiFetch<{ status: 'ok'; items: GrammarBankListItem[] }>(`/grammar-banks${toQS(filters)}`);
  return r.items;
}

export async function getCatalogs(): Promise<{ categories: GrammarCategory[]; statements: GrammarStatement[] }> {
  const r = await apiFetch<{ status: 'ok'; categories: GrammarCategory[]; statements: GrammarStatement[] }>(
    '/grammar-banks/catalogs'
  );
  return { categories: r.categories, statements: r.statements };
}

export async function getGrammarBankBySlug(slug: string): Promise<GrammarBankDetail> {
  const r = await apiFetch<{ status: 'ok'; item: GrammarBankListItem; activities: GrammarBankActivity[] }>(
    `/grammar-banks/${encodeURIComponent(slug)}`
  );
  return { item: r.item, activities: r.activities };
}

export interface CreateGrammarBankInput {
  title: string;
  slug: string;
  content?: string | null;
  cover_image_url?: string | null;
  level_id?: number | null;
  category_id?: number | null;
  is_published?: boolean;
}

export async function adminCreateGrammarBank(input: CreateGrammarBankInput): Promise<{ id: number }> {
  return apiFetch<{ status: 'ok'; id: number }>('/grammar-banks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function adminUpdateGrammarBank(id: number, patch: Partial<CreateGrammarBankInput>): Promise<void> {
  await apiFetch(`/grammar-banks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function adminDeleteGrammarBank(id: number): Promise<void> {
  await apiFetch(`/grammar-banks/${id}`, { method: 'DELETE' });
}

export interface ActivityInput {
  position: number;
  statement_id?: number | null;
  content?: string | null;
  quiz_id?: number | null;
}

export async function adminReplaceActivities(id: number, activities: ActivityInput[]): Promise<void> {
  await apiFetch(`/grammar-banks/${id}/activities`, {
    method: 'PUT',
    body: JSON.stringify({ activities }),
  });
}
